import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { formatCurrency, getMonthName, getCurrentYear } from '../lib/utils';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { PieChart as PieChartIcon, TrendingDown } from 'lucide-react';

const COLORS = ['#FF6B6B', '#00CCBC', '#06C167', '#F59E0B', '#8B5CF6', '#EC4899', '#3B82F6', '#14B8A6', '#F97316', '#6366F1', '#EF4444', '#10B981'];

export function DashboardDepensesPage() {
  const { api } = useAuth();
  const [year, setYear] = useState(getCurrentYear());
  const [month, setMonth] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = { annee: year };
        if (month) params.mois = month;
        const res = await api.get('/dashboard/kpis', { params });
        setData(res.data);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [year, month, api]);

  if (loading || !data) {
    return (
      <div className="h-96 flex items-center justify-center">
        <p className="text-muted-foreground">Chargement...</p>
      </div>
    );
  }

  const categories = (data.depenses_par_categorie || []).filter(c => c.categorie !== 'A Catégoriser');
  const totalDepenses = categories.reduce((s, c) => s + c.total, 0);
  const caReel = data.ca_reel || 0;
  const pctGlobal = caReel > 0 ? (totalDepenses / caReel * 100).toFixed(1) : 0;

  const chartData = categories.map((c, i) => ({
    ...c,
    fill: COLORS[i % COLORS.length],
    pct_ca: c.pct_ca || (caReel > 0 ? +(c.total / caReel * 100).toFixed(1) : 0)
  }));

  const pieData = chartData.filter(d => d.total > 0);

  return (
    <div className="space-y-8" data-testid="dashboard-depenses">
      <div className="module-header">
        <div>
          <h1 className="module-title flex items-center gap-3">
            <PieChartIcon className="w-8 h-8 text-primary" />
            Dashboard Depenses
          </h1>
          <p className="text-muted-foreground mt-1">Analyse des depenses en % du chiffre d'affaires</p>
        </div>
        <div className="flex gap-2">
          <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger className="w-[120px]" data-testid="year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026, 2027, 2028, 2029, 2030].map(y => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={month?.toString() || 'all'} onValueChange={(v) => setMonth(v === 'all' ? null : parseInt(v))}>
            <SelectTrigger className="w-[140px]" data-testid="month-select">
              <SelectValue placeholder="Tous les mois" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les mois</SelectItem>
              {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                <SelectItem key={m} value={m.toString()}>{getMonthName(m)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="stat-card">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground mb-1">CA Reel</p>
            <p className="text-2xl font-bold tabular-nums" data-testid="ca-reel-value">{formatCurrency(caReel)}</p>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground mb-1">Total Depenses</p>
            <p className="text-2xl font-bold tabular-nums text-red-600" data-testid="total-depenses-value">{formatCurrency(totalDepenses)}</p>
          </CardContent>
        </Card>
        <Card className="stat-card border-amber-200 dark:border-amber-800">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground mb-1">Depenses / CA</p>
            <p className="text-2xl font-bold tabular-nums text-amber-600" data-testid="pct-global-value">{pctGlobal}%</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Bar Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingDown className="w-5 h-5" />
              % du CA par categorie
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[350px]">
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} layout="vertical" margin={{ left: 120, right: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis type="number" tickFormatter={(v) => `${v}%`} className="text-xs" />
                    <YAxis type="category" dataKey="categorie" className="text-xs" width={110} tick={{ fontSize: 11 }} />
                    <Tooltip
                      formatter={(value, name) => [`${value}%`, '% du CA']}
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar dataKey="pct_ca" name="% du CA" radius={[0, 4, 4, 0]}>
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground">Aucune donnee</div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Repartition des depenses</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[350px]">
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={65}
                      outerRadius={110}
                      paddingAngle={3}
                      dataKey="total"
                      nameKey="categorie"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground">Aucune donnee</div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detail Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Detail par categorie</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Categorie</TableHead>
                <TableHead className="text-center">Nb transactions</TableHead>
                <TableHead className="text-right">Montant</TableHead>
                <TableHead className="text-right">% du CA</TableHead>
                <TableHead className="text-right">TVA deductible</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {categories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    Aucune depense categorisee pour cette periode
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {categories.map((cat, i) => (
                    <TableRow key={cat.categorie} data-testid={`expense-row-${i}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                          <span className="font-medium">{cat.categorie}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="secondary">{cat.count}</Badge>
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-medium text-red-600">
                        {formatCurrency(cat.total)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant={cat.pct_ca > 30 ? 'destructive' : cat.pct_ca > 15 ? 'outline' : 'secondary'}>
                          {cat.pct_ca}%
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {formatCurrency(cat.tva_deductible)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Total row */}
                  <TableRow className="border-t-2 font-bold">
                    <TableCell>Total</TableCell>
                    <TableCell className="text-center">{categories.reduce((s, c) => s + c.count, 0)}</TableCell>
                    <TableCell className="text-right tabular-nums text-red-600">{formatCurrency(totalDepenses)}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant="outline" className="font-bold">{pctGlobal}%</Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCurrency(categories.reduce((s, c) => s + c.tva_deductible, 0))}
                    </TableCell>
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
